import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PRODUCT_SLUG } from "@types";
import SwiperCore from "swiper";
import { ICardTab } from "types/card-tab";

interface Props {
  slug: PRODUCT_SLUG;
  // eslint-disable-next-line no-unused-vars
  onClick?: (slug: PRODUCT_SLUG, category: ICardTab) => void;
  tabs?: ICardTab[];
}

const useHomeTabs = ({ slug, onClick, tabs = [] }: Props) => {
  const listRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const [swiper, setSwiper] = useState<SwiperCore | null>(null);

  const initialActiveId = useMemo(
    () => tabs.find((t) => t.active)?.id ?? tabs[0]?.id ?? "",
    [tabs]
  );

  const [activeId, setActiveId] = useState<string | number>(initialActiveId);

  useEffect(() => {
    setActiveId(initialActiveId);
  }, [initialActiveId]);

  const updateIndicator = useCallback(() => {
    if (!activeRef.current || !listRef.current || !indicatorRef.current) {
      return;
    }

    const tabRect = activeRef.current.getBoundingClientRect();
    indicatorRef.current.style.width = `${tabRect.width}px`;
    const listRect = listRef.current.getBoundingClientRect();
    indicatorRef.current.style.left = `${tabRect.left - listRect.left}px`;
  }, []);

  useEffect(() => {
    const handler = () =>
      requestAnimationFrame(() => {
        updateIndicator();
      });

    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      updateIndicator();
    });
  }, [activeId, updateIndicator]);

  const handleTabClick = useCallback(
    (tab: ICardTab) => {
      setActiveId(tab.id);
      onClick?.(slug, tab);
      const swiperIndex = tabs.findIndex((t) => t.id === tab.id) || 0;
      swiper?.slideTo(swiperIndex, 0);
      requestAnimationFrame(() => {
        updateIndicator();
      });
    },
    [slug, onClick, updateIndicator]
  );

  const hasTabs = tabs.length > 0;

  return {
    listRef,
    activeRef,
    indicatorRef,
    swiper,
    setSwiper,
    activeId,
    updateIndicator,
    handleTabClick,
    hasTabs,
  };
};

export default useHomeTabs;
